import { makeStyles, mergeClasses, shorthands, Text, tokens } from '@fluentui/react-components';
import React from 'react';
import { getPageContent, useAppState } from '../context';
import AppPageView3D from './page/view3d';
import EditButton from '../standard-components/editButton';
import AppInspectionRecordContainer from './page/inspectionRecords';
import { isContentModel, isContentText } from '@gkn-ili/views/dist/PageContent';
import AppActivityGroupNav from './page/activityGroupNav';
import { useView3DCamera } from '../context/context3D';
import { isPageReferenceActivity, isPageReferenceActivityGroup } from '@gkn-ili/views/dist/OrderDocumentation';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'stretch',
    alignContent: 'stretch',
  },
  documentationContainer: {
    flex: '1',
    minHeight: '0%',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('5px'),
  },

  title: {
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    position: 'relative',
    ...shorthands.margin('5px', '10px'),
  },
  content: {
    ...shorthands.margin('5px', '10px'),
    flex: '1',
    minHeight: '0%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  description: {
    fontSize: tokens.fontSizeBase300,
    textJustify: 'inter-word',
  },
  stateMarker: {
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: '64px',
    lineHeight: '80px',
    color: 'white',
  },
  stateMarkerAccepted: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  stateMarkerNotAccepted: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  stateMarkerNoValue: {
    backgroundColor: tokens.colorBackgroundOverlay,
  },
  image: {
    display: 'block',
    height: 'auto',
    width: 'auto',
    maxHeight: '100%',
    maxWidth: '100%',
    minHeight: 0,
    minWidth: 0,
    objectFit: 'contain',
  },
});

export default function AppMeasurement(props: React.PropsWithoutRef<{ className: string }>): JSX.Element {
  const styles = useStyles();
  const appState = useAppState();
  const view3DCamera = useView3DCamera();

  const page = getPageContent(appState.orderView, appState.activePage);
  const name = page.name;

  const textContentComponent = isContentText(page)
    ? page.content.text.map((text, i) =>
        typeof text === 'string' ? (
          <Text key={i} className={styles.description}>
            {text}
          </Text>
        ) : undefined,
      )
    : undefined;
  const modelContentComponent = React.useMemo(
    () =>
      appState.model && (
        <AppPageView3D model={appState.model} view={isContentModel(page) ? page.content.view : undefined} />
      ),
    [appState.model, page],
  );

  const editButtonComponent = React.useMemo(
    () => (
      <EditButton
        dialogPayload={() =>
          isPageReferenceActivity(appState.activePage)
            ? {
                type: 'editActivity',
                data: { activityReference: appState.activePage, cameraView: view3DCamera.getState() },
              }
            : isPageReferenceActivityGroup(appState.activePage)
              ? {
                  type: 'editActivityGroup',
                  data: { activityGroupReference: appState.activePage, cameraView: view3DCamera.getState() },
                }
              : {
                  type: 'editStep',
                  data: { stepReference: appState.activePage, cameraView: view3DCamera.getState() },
                }
        }
      />
    ),
    [appState.activePage],
  );
  return (
    <div className={mergeClasses(props.className, styles.root)}>
      <div className={styles.documentationContainer}>
        <div className={styles.title}>
          {name}
          {editButtonComponent}
        </div>
        <div className={styles.content}>
          {textContentComponent}
          <div hidden={!isContentModel(page)} style={{ flex: '1', minHeight: '0%' }}>
            {modelContentComponent}
          </div>
          <AppActivityGroupNav />
        </div>
      </div>
      <AppInspectionRecordContainer />
    </div>
  );
}
